
import { Navbar } from "@/components/Navbar";
import { OnboardingForm } from "@/components/OnboardingForm";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { useToast } from "@/components/ui/use-toast";

export default function Onboarding() {
  const { user } = useAuth();
  const { onboardingStep, setOnboardingStep, isOnboardingComplete } = useUI();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formTitle, setFormTitle] = useState("Personalize Your Wellness Journey");
  const [formDescription, setFormDescription] = useState(
    "Tell us about yourself so we can create your personalized wellness plan. This will only take a few minutes."
  );

  useEffect(() => {
    // Check if the user is already registered and has completed onboarding
    if (user && isOnboardingComplete) {
      toast({
        title: "Already Onboarded",
        description: "You've already completed the onboarding process.",
      });
      navigate('/dashboard');
      return;
    }
    
    // Get selected plan from localStorage or default to free
    const selectedPlan = localStorage.getItem('selectedPlan');
    const selectedPriceId = localStorage.getItem('selectedPriceId');
    
    if (!selectedPlan || !selectedPriceId) {
      localStorage.setItem('selectedPlan', 'free');
      localStorage.setItem('selectedPriceId', 'free_tier');
      console.log('No plan selected, defaulting to free plan');
    } else {
      console.log(`Using selected plan: ${selectedPlan}, priceId: ${selectedPriceId}`);
      
      toast({
        title: `${selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1)} Plan Selected`,
        description: selectedPlan === 'premium' ? 
          "You've selected our premium wellness plan." : 
          "You've selected our free wellness plan.",
      });
    }
    
    // Reset onboarding step to beginning
    setOnboardingStep(0);
  }, [setOnboardingStep, toast, navigate, user, isOnboardingComplete]);

  useEffect(() => {
    switch (onboardingStep) {
      case 0:
        setFormTitle("Personal Information");
        setFormDescription("Let's get to know you better to personalize your wellness experience.");
        break;
      case 1:
        setFormTitle("Nutrition Information");
        setFormDescription("Tell us about your eating habits and dietary preferences.");
        break;
      case 2:
        setFormTitle("Fitness & Activity");
        setFormDescription("Share your current fitness level and activity patterns.");
        break;
      case 3:
        setFormTitle("Sleep & Recovery");
        setFormDescription("Help us understand your sleep patterns and recovery needs.");
        break;
      case 4:
        setFormTitle("Current Supplementation");
        setFormDescription("Tell us about any supplements you're currently taking.");
        break;
      case 5:
        setFormTitle("Mindfulness Practices");
        setFormDescription("Share your current mindfulness and stress management routines.");
        break;
      case 6:
        setFormTitle("Wellness Goals");
        setFormDescription("Let us know what you want to achieve on your wellness journey.");
        break;
      default:
        setFormTitle("Personalize Your Wellness Journey");
        setFormDescription("Tell us about yourself so we can create your personalized wellness plan.");
    }
  }, [onboardingStep]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-primary/5 to-primary/10 py-12 px-4">
        <div className="container mx-auto">
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-display font-bold tracking-tight mb-3">
              {formTitle}
            </h1>
            <p className="text-muted-foreground max-w-xl mx-auto">
              {formDescription}
            </p>
          </div>
          
          <div className="mb-8">
            <div className="flex justify-center">
              <div className="flex space-x-2 items-center">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div 
                    key={i}
                    className={`rounded-full transition-all duration-300 ${
                      i <= onboardingStep ? "bg-primary w-8 h-2" : "bg-primary/30 w-2 h-2"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <OnboardingForm />
        </div>
      </div>
    </>
  );
}
